import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Book, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useUniversalTracking } from '@/hooks/useUniversalTracking';

interface MeaningMakingExerciseProps {
  onBack: () => void;
}

export const MeaningMakingExercise = ({ onBack }: MeaningMakingExerciseProps) => {
  const { logExercise, isLogging } = useUniversalTracking();
  const [responses, setResponses] = useState({
    beforeAndAfter: '',
    whatChanged: '',
    unexpectedGrowth: '',
    newPriorities: '',
    wisdomGained: '',
    howYouHonorThem: ''
  });

  const handleSave = async () => {
    await logExercise({
      exercise_type: 'meaning_making',
      exercise_name: 'Meaning Reconstruction Journal',
      category: 'grief',
      duration_minutes: 20,
      completion_data: {
        sections_completed: Object.values(responses).filter(v => v.trim()).length
      },
      xp_earned: 25
    });
    toast.success('Your meaning-making reflections have been saved', {
      description: 'Growth through grief is honoured'
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <Button variant="outline" onClick={onBack} className="mb-4">
        ← Back to Grief Tools
      </Button>

      <Card className="bg-gradient-therapeutic border-primary/20">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-secondary/10">
              <Book className="h-6 w-6 text-secondary" />
            </div>
            <div>
              <CardTitle className="text-2xl font-light">Meaning Reconstruction</CardTitle>
              <CardDescription>
                Finding meaning and growth through loss doesn't diminish your grief
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Introduction */}
          <Card className="bg-gradient-calm border-border/50">
            <CardContent className="py-4 space-y-3">
              <p className="text-sm font-medium">The Timeline of Transformation</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Every significant loss creates a "before" and "after" in your life story. This exercise, rooted in 
                <span className="font-medium text-foreground"> Meaning Reconstruction theory</span> (Neimeyer), helps you 
                integrate this loss into your life narrative-not to erase the pain, but to discover how you've been 
                transformed. You're not the same person you were before, and that's both painful and profound.
              </p>
            </CardContent>
          </Card>

          {/* Before and After */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <span className="text-lg">🔄</span>
              Describe your "before" and "after"
            </label>
            <p className="text-xs text-muted-foreground">
              Who were you before this loss? Who are you now? What's different?
            </p>
            <Textarea
              value={responses.beforeAndAfter}
              onChange={(e) => setResponses({ ...responses, beforeAndAfter: e.target.value })}
              placeholder="Before, I took things for granted... After, I notice... Before, I believed... Now I understand..."
              className="min-h-[140px] bg-background/80 leading-relaxed"
            />
          </div>

          {/* What Changed About the World */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <span className="text-lg">🌍</span>
              How has your view of the world changed?
            </label>
            <p className="text-xs text-muted-foreground">
              Loss shatters our assumptions about how the world works. What beliefs were challenged?
            </p>
            <Textarea
              value={responses.whatChanged}
              onChange={(e) => setResponses({ ...responses, whatChanged: e.target.value })}
              placeholder="I thought I had more time... I realised how fragile life is... I learnt that control is an illusion..."
              className="min-h-[120px] bg-background/80 leading-relaxed"
            />
          </div>

          {/* Unexpected Growth */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <span className="text-lg">🌱</span>
              What unexpected growth has emerged?
            </label>
            <p className="text-xs text-muted-foreground">
              This doesn't mean the loss was "worth it"-but has anything grown in the cracks grief created?
            </p>
            <Textarea
              value={responses.unexpectedGrowth}
              onChange={(e) => setResponses({ ...responses, unexpectedGrowth: e.target.value })}
              placeholder="I've become more compassionate, I value my relationships more deeply, I've found strength I didn't know I had..."
              className="min-h-[120px] bg-background/80 leading-relaxed"
            />
          </div>

          {/* New Priorities */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <span className="text-lg">🎯</span>
              How have your priorities or values shifted?
            </label>
            <p className="text-xs text-muted-foreground">
              Grief often clarifies what truly matters. What matters more now? What matters less?
            </p>
            <Textarea
              value={responses.newPriorities}
              onChange={(e) => setResponses({ ...responses, newPriorities: e.target.value })}
              placeholder="I care less about what others think... I prioritise time with loved ones... I'm more honest about my feelings..."
              className="min-h-[120px] bg-background/80 leading-relaxed"
            />
          </div>

          {/* Wisdom Gained */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <span className="text-lg">💎</span>
              What wisdom have you gained through this experience?
            </label>
            <p className="text-xs text-muted-foreground">
              Wisdom that can only come through hardship-what do you now understand that you couldn't before?
            </p>
            <Textarea
              value={responses.wisdomGained}
              onChange={(e) => setResponses({ ...responses, wisdomGained: e.target.value })}
              placeholder="That grief is love with nowhere to go... That it's possible to hold both joy and sorrow... That humans are more resilient than we think..."
              className="min-h-[120px] bg-background/80 leading-relaxed"
            />
          </div>

          {/* How You Honour Them */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <span className="text-lg">🙏</span>
              How do you honour what was lost as you move forward?
            </label>
            <p className="text-xs text-muted-foreground">
              Living fully doesn't betray the loss-it can honour it. How do you carry them/it forward?
            </p>
            <Textarea
              value={responses.howYouHonorThem}
              onChange={(e) => setResponses({ ...responses, howYouHonorThem: e.target.value })}
              placeholder="By living with more intention, by being kinder, by pursuing the dreams they believed in, by letting their love change me..."
              className="min-h-[120px] bg-background/80 leading-relaxed"
            />
          </div>

          {/* R3 Integration */}
          <Card className="bg-gradient-calm border-border/50">
            <CardContent className="py-4 space-y-3">
              <p className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="w-4 h-4" />
                R3 Method: Integrating Loss Into Your Story
              </p>
              <div className="text-xs text-muted-foreground leading-relaxed space-y-2">
                <p>
                  <span className="font-medium text-foreground">Recognise:</span> You're not trying to "get over" the loss-you're 
                  learning to integrate it into your life story in a way that allows both grief and growth to coexist.
                </p>
                <p>
                  <span className="font-medium text-foreground">Release:</span> Can you release the belief that growth through 
                  grief means you didn't love them enough, or that moving forward is a betrayal?
                </p>
                <p>
                  <span className="font-medium text-foreground">Reprogramme:</span> "I can honour what was lost AND continue to grow. 
                  Both can be true. The meaning I make from this loss is mine to shape."
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1">
              Save as Draft
            </Button>
            <Button className="flex-1" onClick={handleSave} disabled={isLogging}>
              <Book className="w-4 h-4 mr-2" />
              {isLogging ? 'Saving...' : 'Save Reflections'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* The Science */}
      <Card className="bg-gradient-calm border-border/50">
        <CardContent className="py-4">
          <div className="space-y-3">
            <p className="text-sm font-medium">Meaning Reconstruction in Grief</p>
            <div className="text-xs text-muted-foreground leading-relaxed space-y-2">
              <p>
                <span className="font-medium text-foreground">Robert Neimeyer's research</span> shows that finding meaning 
                isn't about silver linings or "everything happens for a reason." It's about reconstructing your assumptive 
                world-the beliefs about yourself, others, and how life works-that loss has shattered.
              </p>
              <p>
                <span className="font-medium text-foreground">Post-traumatic growth research</span> (Tedeschi & Calhoun) 
                confirms that many people report positive changes after loss: deeper relationships, new priorities, greater 
                personal strength, appreciation for life, or spiritual growth.
              </p>
              <p>
                <span className="font-medium text-foreground">Important caveat:</span> This doesn't mean the loss was 
                "worth it" or "meant to be." It means humans have an incredible capacity to find meaning even in the 
                most painful experiences-and that capacity is part of how we survive and eventually thrive again.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
